import _object_spread from "@swc/helpers/src/_object_spread.mjs";
import _object_spread_props from "@swc/helpers/src/_object_spread_props.mjs";
import _object_without_properties from "@swc/helpers/src/_object_without_properties.mjs";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
import { useState, useEffect } from "react";
import { Global, css } from "@emotion/react";
import get from "lodash/get";
import { DayPicker } from "react-day-picker";
import { isBefore } from "date-fns/isBefore";
import { isAfter } from "date-fns/isAfter";
import ArrowLast from "@engineering/icons/arrow-last";
/** @todo: Remove the hard dependency on the config file which should only be a dev dependency */ import tailwindConfig from "@silvertours/config-tailwindcss";
var defaultProps = {
    scrollToSelectedMonth: false
};
/**
 * Mimics Tailwinds `theme()` function.
 * @see https://tailwindcss.com/docs/functions-and-directives#theme
 */ var theme = function(property) {
    return get(tailwindConfig, "theme.".concat(property));
};
/**
These global styles required by the `react-day-picker` code are applied using
`styled-jsx` to add them only when this component is used. This also fixes
missing styles in Storybook. Adding them to Storybook using a CSS file via
"import" unfortunately does not work with Tailwind’s `theme()` function.
*/ var globalStyles = /*#__PURE__*/ css(".rdp{position:relative;outline:0;color:", theme("colors.primary"), ";user-select:none;}.rdp-button_reset{margin:0;padding:0;border:0;font:inherit;background:none;appearance:none;}.rdp-months{display:flex;flex-wrap:wrap;justify-content:center;}.rdp-month{margin:1.5rem 1rem 1rem;}.rdp-nav_button{position:absolute;top:1.5rem;width:36px;height:36px;outline:0;color:inherit;-webkit-tap-highlight-color:transparent;cursor:pointer;display:flex;align-items:center;justify-content:center;&:disabled{cursor:not-allowed;}}.rdp-nav_button.rdp-nav_button_previous{left:0.75rem;}.rdp-nav_button.rdp-nav_button_next{right:0.75rem;transform:rotate(0.5turn);}.rdp-caption_label{margin:0.25rem 0;font-size:1.125rem;font-weight:600;text-align:center;text-transform:capitalize;}.rdp-table{border-spacing:0;border-collapse:collapse;}.rdp-head_cell{display:table-cell;padding:8px 0;color:", theme("colors.color6"), ";font-size:0.938rem;font-weight:600;text-align:center;}.rdp-cell{padding:8px 0 0;}.rdp-day{outline:0;width:40px;height:32px;font-size:1.125rem;text-align:center;vertical-align:middle;cursor:pointer;color:", theme("colors.primary"), ";}.rdp-day.rdp-day_outside{color:", theme("colors.color2"), ";cursor:default;}.rdp-day.rdp-day_disabled{color:", theme("colors.color6"), ";cursor:default;}/* selected pick-up/drop-off dates */\n  .rdp-day.rdp-day_selected:not(.rdp-day_disabled,.rdp-day_outside){position:relative;color:", theme("colors.white"), ";}.rdp-day.rdp-day_range_start.rdp-day_selected:not(\n      .rdp-day_disabled,.rdp-day_outside\n    ){background-image:radial-gradient(\n        circle at center,", theme("colors.color1.DEFAULT"), ",", theme("colors.color1.DEFAULT"), " 60%,transparent 65%\n      ),linear-gradient(\n        to right,transparent,transparent 49%,", theme("colors.color2.400"), " 50%\n      );}.rdp-day.rdp-day_range_end.rdp-day_selected:not(\n      .rdp-day_disabled,.rdp-day_outside\n    ){background-image:radial-gradient(\n        circle at center,", theme("colors.color1.DEFAULT"), ",", theme("colors.color1.DEFAULT"), " 60%,transparent 65%\n      ),linear-gradient(\n        to left,transparent,transparent 49%,", theme("colors.color2.400"), " 50%\n      );}.rdp-day.rdp-day_range_start.rdp-day_range_end.rdp-day_selected:not(\n      .rdp-day_disabled,.rdp-day_outside\n    ){background-image:radial-gradient(\n      circle at center,", theme("colors.color1.DEFAULT"), ",", theme("colors.color1.DEFAULT"), " 70%,transparent 71%\n    );}/* range between pick-up and drop-off */\n  .rdp-day.rdp-day_selected:not(\n      .rdp-day_range_start,.rdp-day_range_end,.rdp-day_outside\n    ){color:", theme("colors.primary"), ";background-color:", theme("colors.color2.400"), ";}/* today but not selected */\n  .rdp-day.rdp-day_today:not(.rdp-day_selected){margin:0 0.25rem;width:32px;border:1px solid ", theme("colors.color6"), ";border-radius:100%;}.rdp-vhidden{position:absolute;width:1px;height:1px;margin:-1px;padding:0;border:0;overflow:hidden;clip:rect(0 0 0 0);}@media screen and (min-width:", theme("screens.xl"), "){.rdp-months{justify-content:space-around;}}");
var NavigationIcon = function() {
    return /*#__PURE__*/ _jsx(ArrowLast, {
        className: "hover:fill-primary/80 w-50 h-50"
    });
};
var DatePicker = function(_param) {
    var from = _param.from, to = _param.to, scrollToSelectedMonth = _param.scrollToSelectedMonth, onDatesChange = _param.onDatesChange, props = _object_without_properties(_param, [
        "from",
        "to",
        "scrollToSelectedMonth",
        "onDatesChange"
    ]);
    useEffect(function() {
        var ref, ref1, ref2, ref3;
        if (!scrollToSelectedMonth) {
            return;
        }
        var selectedDay = document.querySelector(".rdp-day_selected");
        var selectedMonthElm = selectedDay === null || selectedDay === void 0 ? void 0 : (ref = selectedDay.parentElement) === null || ref === void 0 ? void 0 : (ref1 = ref.parentElement) === null || ref1 === void 0 ? void 0 : (ref2 = ref1.parentElement) === null || ref2 === void 0 ? void 0 : (ref3 = ref2.parentElement) === null || ref3 === void 0 ? void 0 : ref3.parentElement;
        if (selectedMonthElm) {
            selectedMonthElm.scrollIntoView();
        }
    }, [
        scrollToSelectedMonth
    ]);
    var ref = useState(from || null), selectedFrom = ref[0], setFrom = ref[1];
    var ref1 = useState(to || null), selectedTo = ref1[0], setTo = ref1[1];
    var ref2 = useState(undefined), hoveredTo = ref2[0], setHoveredTo = ref2[1];
    var isSelectingFirstDay = function(date) {
        var isBeforeFirstDay = selectedFrom && isBefore(date, selectedFrom);
        var isRangeSelected = selectedFrom && selectedTo;
        return !selectedFrom || isBeforeFirstDay || isRangeSelected;
    };
    var handleOnDayClick = function(date, modifiers) {
        if (modifiers.disabled) {
            return;
        }
        if (isSelectingFirstDay(date)) {
            setFrom(date);
            setTo(null);
            setHoveredTo(undefined);
        } else {
            setTo(date);
            setHoveredTo(date);
            if (typeof onDatesChange !== "undefined") {
                onDatesChange({
                    from: selectedFrom,
                    to: date
                });
            }
        }
    };
    var getHighlightedDays = function() {
        var highlightRules = [
            function(day) {
                if (selectedFrom && selectedTo) {
                    return isAfter(day, selectedFrom) && isBefore(day, selectedTo);
                }
                return false;
            }, 
        ];
        if (selectedFrom) {
            highlightRules.push(selectedFrom);
        }
        if (selectedFrom && hoveredTo) {
            highlightRules.push({
                from: selectedFrom,
                to: hoveredTo
            });
        }
        if (selectedFrom && selectedTo) {
            highlightRules.push({
                from: selectedFrom,
                to: selectedTo
            });
        }
        return highlightRules;
    };
    var getModifiers = function() {
        var modifiers = {};
        if (selectedFrom) {
            modifiers = _object_spread_props(_object_spread({}, modifiers), {
                from: [
                    selectedFrom
                ]
            });
        }
        if (hoveredTo) {
            modifiers = _object_spread_props(_object_spread({}, modifiers), {
                to: hoveredTo
            });
        } else if (selectedTo) {
            modifiers = _object_spread_props(_object_spread({}, modifiers), {
                to: selectedTo
            });
        }
        return modifiers;
    };
    var handleDayMouseEnter = function(day) {
        if (from && isBefore(day, from) || isSelectingFirstDay(day)) {
            return;
        }
        setHoveredTo(day);
    };
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Global, {
                styles: [
                    globalStyles
                ]
            }),
            /*#__PURE__*/ _jsx(DayPicker, _object_spread({
                components: {
                    IconLeft: NavigationIcon,
                    IconRight: NavigationIcon
                },
                onDayClick: handleOnDayClick,
                onDayMouseEnter: handleDayMouseEnter,
                selected: getHighlightedDays(),
                modifiers: getModifiers(),
                disabled: {
                    before: new Date()
                },
                modifiersClassNames: {
                    outside: "rdp-day_outside",
                    disabled: "rdp-day_disabled",
                    today: "rdp-day_today",
                    from: "rdp-day_range_start",
                    to: "rdp-day_range_end"
                }
            }, props))
        ]
    });
};
DatePicker.defaultProps = defaultProps;
export { DatePicker };
